import {
  GeneratorState,
  QueueType,
  Tag,
  TagGroup,
  TransformTag,
  TransformTagGroup,
} from "@/app/context/GeneratorContext";
import { UserProps } from "@/app/context/store";

import { IS_MAIN_SITE } from "@/components/shared/sites/sites";
import { format } from "date-fns";
import { indexToLocaleKey } from "i18n";

interface AddTag {
  masterTag: string;
  tag: {
    _id: string;
    tagGroups: string[];
    names: { [locale: string]: string };
    savedTag?: boolean;
  };
}

interface HideTag {
  tag: {
    _id: string;
  };
}

export const hasEnoughCredits = (user: UserProps, state: GeneratorState, tagList: TagGroup[]) => {
  let hasEnough = true;
  let creditCost = 0;

  let creditSum = IS_MAIN_SITE
    ? user.credits.ccBill + user.credits.subStar + user.credits.daily
    : user.credits.subStar + user.credits.niche + user.credits.daily;

  creditCost += state.creditCost.generatorCost;
  creditCost += state.queueType === QueueType.Free ? 0 : state.creditCost.premiumCost;

  let numTags = 0;
  for (const tagGroup of tagList) {
    for (const tag of tagGroup.tags) {
      if (tag.selected) {
        numTags += 1;
      }
    }
  }

  if (numTags > state.creditCost.freeTagLimit) {
    const diff = numTags - state.creditCost.freeTagLimit;
    creditCost += diff * state.creditCost.tagCost;
  }

  if (state.customPrompt.length > 0) {
    // console.log(`custom prompt length ${state.customPrompt.length}`);
    let promptMultiplier = Math.ceil(state.customPrompt.length / 10);
    creditCost += promptMultiplier * state.creditCost.promptCost;
  }

  if (state.customNegative.length > 0) {
    // console.log(`custom negative length ${state.customPrompt.length}`);
    let promptMultiplier = Math.ceil(state.customNegative.length / 10);
    creditCost += promptMultiplier * state.creditCost.promptCost;
  }

  if (creditCost > creditSum) {
    hasEnough = false;
  }

  return hasEnough;
};

// transform the tags from the backend into a format that the toggle UI can read
export const transformTags = (tagGroups: TransformTagGroup[], localeIndex: number): TagGroup[] => {
  if (!tagGroups || !Array.isArray(tagGroups)) {
    return [];
  }
  return tagGroups.map((group) => ({
    id: group._id,
    name: group.names[indexToLocaleKey[localeIndex]] || group.names["en-US"],
    type: group.type,
    tags: group.tags.map((tag: TransformTag) => ({
      id: tag._id,
      masterTag: tag.masterTag,
      name: tag.names[indexToLocaleKey[localeIndex]] || tag.names["en-US"],
      selected: false,
    })),
  }));
};

// transform the tags from the backend into a format that the toggle UI can read
export const transformGifTags = (tagGroups: any[], localeIndex: number): TagGroup[] => {
  if (!tagGroups || !Array.isArray(tagGroups)) {
    return [];
  }
  return tagGroups.map((group) => ({
    id: group._id,
    name: group.names[localeIndex].value || group.names[0].value,
    type: group.type,
    tags: group.tags.map((tag: any) => ({
      id: tag._id,
      name: tag.names[localeIndex].value || tag.names[0].value,
      selected: false,
    })),
  }));
};

//randomizer helper function
export const getRandomTagCount = (maxCount: any) => {
  // for example, chances: [10%, 50%, 30%, 7%, 2%, 1%] for 0, 1, 2, 3, 4, 5 tags respectively
  const weights = [0.1, 0.5, 0.3, 0.07, 0.02, 0.01];
  const randomValue = Math.random();
  let accumulatedChance = 0;
  for (let i = 0; i < weights.length; i++) {
    accumulatedChance += weights[i];
    if (randomValue < accumulatedChance) {
      // Make sure not to exceed maxCount
      return Math.min(i, maxCount);
    }
  }
  return 0; // default/fallback
};

export const formatTimeAgo = (date: Date, timeAgoHour: string, timeAgoMin: string) => {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 60) {
    return getTimeAgoMinMessage(diffInMinutes, timeAgoMin);
    //return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return getTimeAgoHrMessage(diffInHours, timeAgoHour);
    //return `${diffInHours}h ago`;
  }

  return format(date, "MMM d, yyyy HH:mm");
};

export const getTimeAgoHrMessage = (timeAgoH: number, timeAgoHour: string): string => {
  const template: string = timeAgoHour || "{timeAgoH}h ago";
  const replacements: Record<string, string | number> = { timeAgoH }; //fix type error

  //replace {timeAgoH} in timeAgoHour string
  return template.replace(/{(\w+)}/g, (_, key: string) => {
    return key in replacements ? `${replacements[key]}` : _;
  });
};

export const getTimeAgoMinMessage = (timeAgoM: number, timeAgoMin: string): string => {
  const template: string = timeAgoMin || "{timeAgoM}m ago";
  const replacements: Record<string, string | number> = { timeAgoM };

  //replace {timeAgoM} in timeAgoMin string
  return template.replace(/{(\w+)}/g, (_, key: string) => {
    return key in replacements ? `${replacements[key]}` : _;
  });
};

export const updateTogglesList = (
  toggles: TagGroup[],
  addTags: AddTag[],
  hideTags: HideTag[],
  localeIndex: number,
): TagGroup[] => {
  const tagsToHide = new Set(hideTags.map((ht) => ht.tag._id));

  // Clone toggles & filter tags to hide
  const newToggles = toggles.map((toggle) => ({
    ...toggle,
    tags: toggle.tags.filter((tag) => !tagsToHide.has(tag.id)).map((tag) => ({ ...tag })),
  }));

  // Tag group map for lookup
  const toggleMap = new Map<string, TagGroup>();
  for (const toggle of newToggles) {
    toggleMap.set(toggle.id, toggle);
  }

  for (const { masterTag, tag } of addTags) {
    const { _id, tagGroups, names } = tag;

    for (const groupId of tagGroups) {
      const toggleGroup = toggleMap.get(groupId);
      if (!toggleGroup) continue;

      // Skip if tag already exists
      if (toggleGroup.tags.some((t) => t.id === _id)) continue;

      const newTag = {
        id: _id,
        masterTag,
        name: names[indexToLocaleKey[localeIndex]] || names["en-US"],
        selected: false,
        savedTag: true,
      };

      // Overwrite masterTag if it exists
      if (masterTag) {
        const masterIndex = toggleGroup.tags.findIndex((t) => t.masterTag === masterTag);
        if (masterIndex !== -1) {
          toggleGroup.tags[masterIndex] = newTag;
          continue;
        }
      }

      // Otherwise add to tag to respective tag grioup
      toggleGroup.tags.push(newTag);
    }
  }

  return newToggles;
};
